import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import NearMeIcon from "@mui/icons-material/NearMe";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Link from "@mui/material/Link";
import type { SxProps, Theme } from "@mui/material/styles";
import { type ReactNode, useId, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { DISPATCH_COLOR_VALUES } from "@/components/Dispatch/dispatchColors";
import { EnumPill } from "@/components/Dispatch/FieldValue";
import type { DriverRouteSummary, RouteStopSummary } from "@/types/driverRoute";
import { zinc } from "@/ui/tokens";
import {
  CONTEXT_ACCENT,
  ContextCaption,
  ContextField,
  ContextSection,
  ContextText,
  contextFieldGridSx,
} from "./ContextBlock";
import { changedAt, NO_VALUE } from "./contextFormat";
import { googleMapsUrl } from "./LocationField";
import {
  ARRIVAL_RISK_VALUES,
  emphasizesHandling,
  formatAppointment,
  formatArrival,
  formatMilesCompleted,
  formatNoteLines,
  formatStopHandling,
  formatStopLegMiles,
  formatStopPlace,
  formatStopRole,
  keepsAppointments,
  knownValue,
  routeLegs,
  STOP_STATE_VALUES,
  shownRisk,
  shownState,
  truckPlacement,
} from "./routeFormat";

/**
 * The Live chip's fill: a deeper orange than the dispatch paint, so white text reads on it. A solid fill sets the
 * stop's handling apart from the tinted, dotted state and risk pills beside it.
 */
export const LIVE_CHIP_FILL = "#ea580c";

/** The word the route head carries when the driver is not on the route yet, or is already off it. */
const LIFECYCLE_WORDS: Record<DriverRouteSummary["lifecycle"], string | null> = {
  current: null,
  nearFuture: "queued",
  recentlyCompleted: "finished",
};

/**
 * The Route section: the route the driver is on and every stop on it, in run order, each with where it stands.
 *
 * The head is two columns: the route's name (or the vendor's id when it has none) with the reference numbers under
 * it, and beside them its progress (see {@link RouteProgress}). Under the head, the dispatcher's notes on the order
 * where the vendor states any, collapsed to one line. Below them, one row per resolved stop: its name, its kind, its
 * state as a pill, and the times that matter for where it stands — arrival and departure once the driver is past it,
 * the ETA, appointment, and arrival risk while it is still ahead. The next stop carries an accent so the eye lands on
 * it. Between the notes and the stops, a strip draws the route leg by leg with the truck on the leg the driver is on
 * (see {@link RouteStrip}). A driver on nothing reads "No current route".
 *
 * Every driver with a channel gets a Check ETA button: under the next stop's times, or at the foot of the section
 * when there is no stop ahead (see {@link CheckEtaButton}).
 */
export default function RouteBlock({
  route,
  onCheckEta = null,
}: {
  route: DriverRouteSummary | null;
  /** Messages the driver to confirm their ETA to the next stop; null hides the Check ETA button. */
  onCheckEta?: CheckEta | null;
}) {
  return (
    <ContextSection
      icon={<RouteOutlinedIcon sx={{ fontSize: 18 }} />}
      title="Route"
      testId={TestIds.dispatch.contextRoute}
    >
      {route ? (
        <RouteBody route={route} onCheckEta={onCheckEta} />
      ) : (
        <>
          <ContextField>
            <ContextText variant="value">No current route</ContextText>
          </ContextField>
          {onCheckEta && <CheckEtaButton stop={null} onCheckEta={onCheckEta} />}
        </>
      )}
    </ContextSection>
  );
}

/** The route's head, its notes, and its stops, under the section's header. */
function RouteBody({ route, onCheckEta }: { route: DriverRouteSummary; onCheckEta: CheckEta | null }) {
  return (
    <>
      <Box sx={contextFieldGridSx}>
        <ContextField label={route.nameIsOrderNumber && route.name !== null ? "Order Number" : undefined}>
          <ContextText variant="value">{route.name ?? route.vendorRouteId}</ContextText>
          <RouteReferences references={route.references} />
        </ContextField>
        <RouteProgress route={route} />
      </Box>
      <RouteNotes key={route.routeId} notes={route.notes} />
      <RouteStrip route={route} />
      {route.stops.length > 0 && (
        <Box
          component="ol"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            borderTop: `1px solid ${zinc[800]}`,
            // Drawn from the list, whose class is fixed: a row's own class varies with its state, so a sibling rule
            // on the row would only match neighbours in the same state.
            "& > li + li": { borderTop: `1px solid ${zinc[800]}` },
          }}
        >
          {route.stops.map((stop, index) => (
            <StopRow
              key={stop.id}
              stop={stop}
              position={index + 1}
              isNext={stop === route.nextStop}
              onCheckEta={stop === route.nextStop ? onCheckEta : null}
            />
          ))}
        </Box>
      )}
      {onCheckEta && !route.nextStop && <CheckEtaButton stop={null} onCheckEta={onCheckEta} />}
    </>
  );
}

/**
 * The head's Progress, a line each in this order: how many of the route's miles are done, a step louder than the lines
 * under it; how many of its stops are behind the driver, after whether the route is queued or finished rather than
 * running; and when it started, or is scheduled to.
 *
 * The miles line is left out until the forecast has measured every leg ahead, except on a finished route, which reads
 * its whole total (see {@link formatMilesCompleted}).
 */
function RouteProgress({ route }: { route: DriverRouteSummary }) {
  const miles = formatMilesCompleted(route);
  const stops = [
    LIFECYCLE_WORDS[route.lifecycle],
    `${route.stopsDone} of ${route.stopsTotal} stops`,
    route.unresolvedStopCount > 0 ? `${route.unresolvedStopCount} unresolved` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const timing = route.startedAt
    ? changedAt(route.startedAt, "started")
    : changedAt(route.scheduledStartAt, "scheduled");
  return (
    <ContextField label="Progress" testId={TestIds.dispatch.contextRouteProgress}>
      {miles && (
        <ContextText variant="value" testId={TestIds.dispatch.contextRouteMiles}>
          {miles}
        </ContextText>
      )}
      <ContextText variant="subtext">{stops}</ContextText>
      {timing && <ContextText variant="subtext">{timing}</ContextText>}
    </ContextField>
  );
}

/** The route's reference numbers, each as its label and value in small print; nothing without any. */
function RouteReferences({ references }: { references: DriverRouteSummary["references"] }) {
  if (references.length === 0) return null;
  return (
    <Box
      data-sb={TestIds.dispatch.contextRouteReferences}
      sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25 }}
    >
      {references.map((reference) => (
        <Box
          component="span"
          key={`${reference.label}:${reference.value}`}
          sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}
        >
          <ContextCaption component="span">{reference.label}</ContextCaption>{" "}
          <ContextText variant="subtext" component="span">
            {reference.value}
          </ContextText>
        </Box>
      ))}
    </Box>
  );
}

/**
 * What the dispatcher wrote on the order, a line each as the vendor stated them; nothing when it states none.
 *
 * Collapsed to its first line until the dispatcher opens it. Trip Comments routinely run to a screenful of boilerplate,
 * which would push the stops — what the tab is usually opened for — below the fold. Keyed by route in the caller, so a
 * dispatcher moving to another driver finds it closed again.
 *
 * The reference numbers are not repeated here: the route's summary already moved them to the head, and what stays is
 * the dispatcher's comments, the load and unload handling, and the loaded miles.
 */
function RouteNotes({ notes }: { notes: string | null }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const lines = formatNoteLines(notes);
  if (lines.length === 0) return null;
  return (
    <Box data-sb={TestIds.dispatch.contextRouteNotes} sx={{ borderTop: `1px solid ${zinc[800]}` }}>
      <ButtonBase
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        sx={{
          width: "100%",
          px: 1.5,
          py: 1,
          gap: 1,
          justifyContent: "flex-start",
          textAlign: "left",
          "&:hover": { bgcolor: zinc[800] },
        }}
      >
        <ContextCaption component="span">Notes</ContextCaption>
        {!open && (
          <ContextText
            variant="subtext"
            component="span"
            sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {lines[0]}
          </ContextText>
        )}
        <ExpandMoreIcon
          sx={{
            ml: "auto",
            flex: "none",
            fontSize: 18,
            color: zinc[400],
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms",
          }}
        />
      </ButtonBase>
      <Collapse in={open} id={bodyId}>
        <ContextText variant="subtext" sx={{ px: 1.5, pb: 1, whiteSpace: "pre-line" }}>
          {lines.join("\n")}
        </ContextText>
      </Collapse>
    </Box>
  );
}

/** A stop's handling as a solid chip: white text on the Live fill, with no dot, so it never reads as a state pill. */
function HandlingChip({ label }: { label: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        height: 20,
        borderRadius: 10,
        bgcolor: LIVE_CHIP_FILL,
        color: "white",
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

/**
 * One stop down the route: its position, name, kind, handling, state, where it is, and the times that place it.
 *
 * The kind reads "Next · Delivery · Drv assist", each part only where it applies. A live stop's handling is a solid
 * orange chip after the kind instead, and a driver-assist word is marked `strong` in heavier type, so the stops that hold the
 * driver stand out. Where it is reads as "City, ST" and a Map link, each only when the vendor stated what it needs: the
 * link opens the vendor's own coordinates and is never built from the address, so a stop the vendor did not locate has
 * none.
 */
export function StopRow({
  stop,
  position,
  isNext,
  onCheckEta = null,
}: {
  stop: RouteStopSummary;
  position: number;
  isNext: boolean;
  /** Messages the driver to confirm their ETA to this stop; null when the row offers no ETA check. */
  onCheckEta?: CheckEta | null;
}) {
  const kind = [isNext ? "Next" : "", formatStopRole(stop)].filter(Boolean).join(" · ");
  const handling = formatStopHandling(stop);
  const chip = stop.handling === "live";
  const inline = chip ? "" : handling;
  const emphasized = emphasizesHandling(stop.handling);
  const place = formatStopPlace(stop);
  const state = shownState(stop);
  const mapsUrl = stop.latitude != null && stop.longitude != null ? googleMapsUrl(stop.latitude, stop.longitude) : null;
  return (
    <Box
      component="li"
      data-sb={TestIds.dispatch.contextStop}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: 1.5,
        py: 1,
        minWidth: 0,
        borderLeft: `2px solid ${isNext ? CONTEXT_ACCENT : "transparent"}`,
        opacity: stop.done ? 0.7 : 1,
      }}
    >
      <Box
        sx={{
          flex: "none",
          width: 20,
          height: 20,
          mt: "1px",
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: isNext ? "black" : zinc[300],
          bgcolor: isNext ? CONTEXT_ACCENT : zinc[800],
        }}
      >
        {position}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <ContextText variant="value" component="span">
            {stop.name ?? stop.address ?? NO_VALUE}
          </ContextText>
          {(kind || inline) && (
            <ContextText
              variant="subtext"
              component="span"
              sx={{ flex: "none", color: isNext ? CONTEXT_ACCENT : zinc[500] }}
            >
              {kind}
              {kind && inline && " · "}
              {inline && (
                <Box
                  component={emphasized ? "strong" : "span"}
                  data-sb={TestIds.dispatch.contextStopHandling}
                  data-handling={stop.handling}
                  sx={emphasized ? { fontWeight: 600 } : undefined}
                >
                  {inline}
                </Box>
              )}
            </ContextText>
          )}
          {chip && (
            <Box
              component="span"
              data-sb={TestIds.dispatch.contextStopHandling}
              data-handling={stop.handling}
              sx={{ flex: "none", display: "inline-flex" }}
            >
              <HandlingChip label={handling} />
            </Box>
          )}
          <Box sx={{ ml: "auto", flex: "none" }}>
            <EnumPill value={state} known={knownValue(STOP_STATE_VALUES, state)} />
          </Box>
        </Box>
        {(place || mapsUrl) && (
          <ContextText variant="subtext" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {place && <span data-sb={TestIds.dispatch.contextStopPlace}>{place}</span>}
            {place && mapsUrl && <span aria-hidden>·</span>}
            {mapsUrl && (
              <Link
                data-sb={TestIds.dispatch.contextStopMap}
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  color: "var(--color-brand-primary-400)",
                }}
              >
                Map
                <OpenInNewIcon sx={{ fontSize: 12 }} />
              </Link>
            )}
          </ContextText>
        )}
        <StopTimes stop={stop} onCheckEta={onCheckEta} />
      </Box>
    </Box>
  );
}

/**
 * The route as a strip between the notes and the stops: one segment per leg, sized by its miles within floors, with
 * fuel stops and waypoints spaced evenly along the span to the next pickup or delivery (see {@link routeLegs}), each
 * ending at a marker for its stop (see {@link StopMarker}), and the truck in the middle of the
 * leg the driver is on, or on the stop they have reached (see {@link truckPlacement}). Only the leg is claimed: the
 * forecast's progress along one is a guess from stated miles rather than a measured position, so the strip does not
 * draw it. On the shortest legs the truck overhangs the marker ahead, which stays on top so the stop still reads, and a
 * leg is never narrower than its marker: the line gives way first, so markers never pile up. Nothing on a route with
 * no resolved stops.
 */
function RouteStrip({ route }: { route: DriverRouteSummary }) {
  const legs = routeLegs(route);
  if (legs.length === 0) return null;
  const truck = truckPlacement(route);
  return (
    <Box sx={{ borderTop: `1px solid ${zinc[800]}`, px: 1.5, py: 1 }}>
      <Box data-sb={TestIds.dispatch.contextRouteStrip} sx={{ display: "flex", alignItems: "center", height: 24 }}>
        {legs.map(({ stop, share }, index) => {
          const truckOnLeg = truck?.at === "beforeStop" && truck.stopId === stop.id;
          const truckAtStop = truck?.at === "stop" && truck.stopId === stop.id;
          return (
            <Box
              key={stop.id}
              sx={{ flex: share, minWidth: 16, display: "flex", alignItems: "center", position: "relative" }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  position: "relative",
                  background: legLine(stop.done || truckAtStop, truckOnLeg),
                }}
              >
                {truckOnLeg && <TruckMarker />}
              </Box>
              <StopMarker stop={stop} position={index + 1} isNext={stop === route.nextStop} hasTruck={truckAtStop} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * A leg's line: the accent once driven, dim while ahead, and half and half under the truck.
 *
 * A leg is driven once its stop is done or the truck has reached the stop, so a stop the driver has checked in at but
 * not left reads its whole leg as driven.
 */
function legLine(driven: boolean, truckOnLeg: boolean): string {
  if (driven) return CONTEXT_ACCENT;
  if (truckOnLeg) return `linear-gradient(to right, ${CONTEXT_ACCENT} 50%, ${zinc[700]} 50%)`;
  return zinc[700];
}

/**
 * A stop on the strip: its number as the rows below carry it, a pump for a fuel stop, a ring for a waypoint. Filled in
 * the accent once the driver is past it, ringed in the accent while it is the next stop, dim while further ahead.
 */
function StopMarker({
  stop,
  position,
  isNext,
  hasTruck,
}: {
  stop: RouteStopSummary;
  position: number;
  isNext: boolean;
  /** The driver is at this stop, reached and not yet left (see {@link truckPlacement}). */
  hasTruck: boolean;
}) {
  const tone = stop.done || isNext ? CONTEXT_ACCENT : zinc[500];
  let face: ReactNode = null;
  let faceSx: SxProps<Theme>;
  if (stop.role === "fuel") {
    face = <LocalGasStationOutlinedIcon sx={{ fontSize: 14 }} />;
    faceSx = { color: tone };
  } else if (stop.role === "pickup" || stop.role === "delivery") {
    face = position;
    if (stop.done) {
      faceSx = { bgcolor: CONTEXT_ACCENT, color: "black" };
    } else {
      faceSx = { bgcolor: zinc[800], color: tone, boxShadow: isNext ? `inset 0 0 0 2px ${CONTEXT_ACCENT}` : "none" };
    }
  } else {
    faceSx = { boxShadow: `inset 0 0 0 2px ${tone}`, bgcolor: stop.done ? CONTEXT_ACCENT : "transparent" };
  }
  // Sized to the marker itself, so a truck centered in this box sits on the marker rather than beside it; raised above
  // a truck overhanging from the leg before it.
  return (
    <Box sx={{ position: "relative", flex: "none", display: "flex", width: 16, height: 16, zIndex: 1 }}>
      <Box
        data-sb={TestIds.dispatch.contextRouteStop}
        data-done={stop.done}
        data-role={stop.role ?? "stop"}
        aria-hidden
        sx={[
          {
            width: 16,
            height: 16,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.5625rem",
            fontWeight: 700,
          },
          ...(Array.isArray(faceSx) ? faceSx : [faceSx]),
        ]}
      >
        {face}
      </Box>
      {hasTruck && <TruckMarker />}
    </Box>
  );
}

/** The truck, centered on whatever it sits in: a leg's line, or a stop's marker. */
function TruckMarker() {
  return (
    <Box
      component="span"
      data-sb={TestIds.dispatch.contextTruck}
      aria-label="The truck is here"
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "inline-flex",
        p: "2px",
        borderRadius: "50%",
        color: CONTEXT_ACCENT,
        bgcolor: zinc[900],
      }}
    >
      <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />
    </Box>
  );
}

/**
 * The times that place a stop: arrival and departure once past, the ETA and appointment with the risk while ahead.
 *
 * The appointment is the stop's own window before the forecast's, in the stop's zone (see {@link formatAppointment}),
 * and the ETA beside it reads in the same zone with the same note (see {@link formatArrival}).
 * A fuel stop shows its state only: no ETA, no appointment, and no arrival-risk pill (see {@link keepsAppointments}).
 *
 * Also the miles of the leg ending at the stop when the forecast charged one, until the driver is seen to reach it
 * (see {@link formatStopLegMiles}).
 *
 * The next stop carries the Check ETA button under its times (see {@link CheckEtaButton}).
 */
function StopTimes({ stop, onCheckEta }: { stop: RouteStopSummary; onCheckEta: CheckEta | null }) {
  const zone = { timeZone: stop.timeZone };
  const appointment = keepsAppointments(stop.role) ? formatAppointment(stop.appointment, zone) : null;
  const words = stop.done
    ? [changedAt(stop.arrivedAt, "arrived"), changedAt(stop.departedAt, "departed")]
    : [formatArrival(stop, appointment ? zone : { timeZone: null }), appointment];
  words.push(formatStopLegMiles(stop));
  const line = words.filter(Boolean).join(" · ");
  const risk = shownRisk(stop);
  if (!line && !risk && !onCheckEta) return null;
  return (
    <>
      {(line || risk) && (
        <ContextText
          variant="subtext"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.25 }}
        >
          {line}
          {risk && <EnumPill value={risk} known={knownValue(ARRIVAL_RISK_VALUES, risk)} />}
        </ContextText>
      )}
      {onCheckEta && <CheckEtaButton stop={stop} onCheckEta={onCheckEta} />}
    </>
  );
}

/** Messages a driver to confirm their ETA to a stop, or to their next one when none is known (see `useCheckEta`). */
type CheckEta = (stop: RouteStopSummary | null) => Promise<void>;

/** Where a Check ETA press stands. */
type CheckEtaStatus = "idle" | "sending" | "sent" | "failed";

/** The words the Check ETA button shows at each status. */
const CHECK_ETA_LABELS: Record<CheckEtaStatus, string> = {
  idle: "Check ETA",
  sending: "Sending…",
  sent: "Sent",
  failed: "Retry Check ETA",
};

/** The Check ETA button's fill, the app's action blue, and its hover. */
const CHECK_ETA_BLUE = "#2563eb";
const CHECK_ETA_BLUE_HOVER = "#1d4ed8";

/**
 * The Check ETA button, under the next stop's times or alone when there is none: one press sends the driver the check (see `checkEtaMessage`),
 * then the button reads "Sent" and holds still so a second press cannot send it twice. A failed send reads "Retry
 * Check ETA" in red and can be pressed again.
 */
function CheckEtaButton({ stop, onCheckEta }: { stop: RouteStopSummary | null; onCheckEta: CheckEta }) {
  const [status, setStatus] = useState<CheckEtaStatus>("idle");
  const press = async () => {
    setStatus("sending");
    try {
      await onCheckEta(stop);
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  };
  return (
    <ButtonBase
      data-sb={TestIds.dispatch.contextStopCheckEta}
      disabled={status === "sending" || status === "sent"}
      onClick={() => void press()}
      sx={{
        mt: 1,
        height: 30,
        px: 1.5,
        gap: 0.75,
        borderRadius: 1.5,
        fontSize: "0.8125rem",
        fontWeight: 600,
        color: status === "failed" ? DISPATCH_COLOR_VALUES.red : "white",
        bgcolor: status === "failed" ? zinc[800] : CHECK_ETA_BLUE,
        border: status === "failed" ? `1px solid ${DISPATCH_COLOR_VALUES.red}` : "1px solid transparent",
        "&:hover": { bgcolor: status === "failed" ? zinc[700] : CHECK_ETA_BLUE_HOVER },
        "&.Mui-disabled": { color: zinc[400], bgcolor: zinc[800] },
      }}
    >
      <NearMeIcon sx={{ fontSize: 16 }} />
      {CHECK_ETA_LABELS[status]}
    </ButtonBase>
  );
}
